import React from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Skeleton,
    Tooltip,
} from '@mui/material';
import { metricAvailabilityState } from '../../../utils/kpiDisplayUtils';

/**
 * Shared KPI metric card (design-review #75 prep).
 *
 * @param {object} props
 * @param {'hero'|'compact'|'secondary'} props.variant
 * @param {string} props.title
 * @param {string|number} props.value
 * @param {string} [props.subtitle]
 * @param {string} [props.testId]
 * @param {string} [props.availability] - ok | unavailable | zero | loading
 * @param {string} [props.unavailableReason]
 * @param {boolean} [props.primary] - design-review #5/#6 single hero
 * @param {Function} [props.onClick]
 * @param {React.ReactNode} [props.trend]
 * @param {React.ReactNode} [props.headerAdornment] - Optional icon or control (top-right)
 * @param {string} [props.kpiKey] - data-kpi-key for KPI audit tests
 * @param {string} [props.dataSource] - data-kpi-source for KPI audit tests
 * @param {string} [props.zeroContext] - zero state context copy (design-review #81)
 * @param {React.ReactNode} [props.children]
 */
const MetricCard = ({
    variant = 'compact',
    title,
    value,
    subtitle,
    testId = null,
    availability = null,
    unavailableReason = 'Data not available for this metric yet.',
    zeroContext = 'Zero in selected period.',
    primary = false,
    onClick = null,
    trend = null,
    headerAdornment = null,
    kpiKey = null,
    dataSource = null,
    children = null,
    color = 'primary',
}) => {
    const resolvedAvailability = availability
        || metricAvailabilityState({ value });

    const isHero = variant === 'hero' || primary;
    const isClickable = Boolean(onClick);
    const displayValue = resolvedAvailability === 'unavailable'
        ? '—'
        : (value ?? 'N/A');

    return (
        <Card
            data-testid={testId}
            data-kpi-key={kpiKey}
            data-kpi-source={dataSource}
            data-availability={resolvedAvailability}
            data-hero-primary={primary ? 'true' : undefined}
            data-clickable={isClickable ? 'true' : undefined}
            aria-label={isClickable && title ? `${title} KPI` : undefined}
            onClick={onClick}
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
            sx={{
                height: '100%',
                width: '100%',
                cursor: isClickable ? 'pointer' : 'default',
                border: isHero || primary ? 2 : 1,
                borderStyle: 'solid',
                borderColor: primary ? `${color}.main` : 'divider',
                background: 'background.paper',
                transition: isClickable ? 'box-shadow 0.2s ease' : 'none',
                '&:hover': isClickable ? { boxShadow: 4 } : {},
                '&:focus-visible': isClickable
                    ? { outline: '2px solid', outlineColor: `${color}.main`, outlineOffset: 2 }
                    : {},
            }}
        >
            <CardContent sx={{ p: isHero ? 3 : 2 }}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 0.5,
                }}
                >
                    <Typography
                        variant={isHero ? 'subtitle1' : 'subtitle2'}
                        color="text.secondary"
                        sx={{ fontWeight: 600 }}
                    >
                        {title}
                    </Typography>
                    {headerAdornment}
                </Box>

                {resolvedAvailability === 'loading' ? (
                    <Skeleton variant="text" width="60%" height={isHero ? 48 : 32} />
                ) : (
                    <Tooltip
                        title={resolvedAvailability === 'unavailable' ? unavailableReason : ''}
                        disableHoverListener={resolvedAvailability !== 'unavailable'}
                    >
                        <Typography
                            variant={isHero ? 'h4' : 'h5'}
                            sx={{ fontWeight: 700, color: `${color}.dark`, mb: 0.5 }}
                        >
                            {displayValue}
                        </Typography>
                    </Tooltip>
                )}

                {subtitle && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {subtitle}
                    </Typography>
                )}

                {resolvedAvailability === 'zero' && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        {zeroContext}
                    </Typography>
                )}

                {resolvedAvailability === 'unavailable' && (
                    <Typography variant="caption" color="text.secondary" display="block">
                        {unavailableReason}
                    </Typography>
                )}

                {trend}
                {children}
            </CardContent>
        </Card>
    );
};

export default MetricCard;
